/**
 * Dashboard - Main Application View
 * ----------------------------------
 * Thin orchestrator. Map-specific state lives in local hooks.
 * Global tray state (branches, scrubber, starred, recent) comes from TrayContext.
 *
 * v9.2 Changes:
 *  - Fetches userCohortIds (GET /api/cohorts) and passes it into useFilters
 *    so the "My Classes" quick filter (added in MapControls.jsx / useFilters.js)
 *    has data to filter against. Independent, non-blocking fetch — failure
 *    just means the filter matches nothing, same as any other quick filter
 *    would if its underlying data were empty.
 *
 * v9.3 Changes (October 2026):
 *  - Links on the map: useMapLinks fetches mention lines ([[p:ID]]
 *    backlinks) and Use echoes and passes them to CosmicMap.
 *  - "Show links" pill (bottom-left) controls MENTION LINES: off = only for
 *    the project you click, on = every line faintly visible.
 *  - Use echoes are opt-in via "Show Use echoes" in the filter menu
 *    (MapControls). Off = never drawn.
 *
 * Place at: src/pages/Dashboard.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// No lucide icons are used directly in this file any more — every icon
// moved out with the presentational components it belonged to.
import toast from 'react-hot-toast';

import { cn } from '../utils/cn';
import { useAuth } from '../context/AuthContext';
import { useTray } from '../context/TrayContext';
import { useMapData } from '../hooks';
import { usePlatform } from '../context/PlatformContext';
import { projectsAPI, api } from './api';
import { getProjectZone } from '../config/zones';

import useMapViewport from '../hooks/useMapViewport';
import useLineage from '../hooks/useLineage';
import useSelection from '../hooks/useSelection';
import useFilters from '../hooks/useFilters';
import useOverlays from '../hooks/useOverlays';
import useLens from '../hooks/useLens';
import useMapController from '../hooks/useMapController';
import useMapLinks from '../hooks/useMapLinks';

import CosmicMap from '../components/map/CosmicMap';
import MapGuidedTour, { useTourState } from '../components/map/MapGuidedTour';

import CrossZoneCelebration from '../components/map/CrossZoneCelebration';
import MapControls from './MapControls';
import ProjectDetailOverlay from '../components/overlays/ProjectDetailOverlay';
import ChallengeDetailOverlay from '../components/overlays/ChallengeDetailOverlay';
import CreateProjectOverlay from '../components/overlays/CreateProjectOverlay';
import CreateChallengeOverlay from '../components/overlays/CreateChallengeOverlay';
import CurateOverlay from '../components/overlays/CurateOverlay';
import OverlayErrorBoundary from '../components/overlays/OverlayErrorBoundary';
import { Button, Spinner } from '../components/ui';
import MessagingDrawer from '../components/MessagingDrawer';
import useDeepLink from '../hooks/useDeepLink';
import SelectionTray from '../components/map/SelectionTray';

// Presentational map components. Extracted from this file in September
// 2026 — they take props and render, share no state with the page, and
// were only here because that is where they were first written.
// ConnectionInfoCard in particular is now available to every map surface,
// not just this one.
import MapLegend from '../components/map/MapLegend';
import ConnectionInfoCard from '../components/map/ConnectionInfoCard';
import GettingStartedCard from '../components/map/GettingStartedCard';
import MultiSelectActionBar from '../components/map/MultiSelectActionBar';


// ============================================================================
// MAIN DASHBOARD
// ============================================================================

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  // ── Global tray state from TrayContext ──────────────────────────────────
  const { branches, scrubber, starred, recent, searchProjects } = useTray();

  // Read global data from PlatformContext (single fetch, shared across all pages)
  const platform = usePlatform();
  const { mapData: platformMapData, dataLoading: mapLoading, dataError: mapError, refetchData: refetch } = platform;

  // Backward-compatible alias so nothing else in Dashboard needs to change
  const mapData = platformMapData;
  const { showTour, completeTour } = useTourState(user?.id);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [celebration, setCelebration]       = useState(null);
  const [isTrayExpanded, setIsTrayExpanded] = useState(false);

  // ── Map-local state ────────────────────────────────────────────────────
  const viewport  = useMapViewport();
  const lineage   = useLineage(mapData.projects || []);
  const selection = useSelection(mapData.projects || []);
  const platformFilters = platform.filters;

  // ── My cohort IDs (for the "My Classes" quick filter) ──────────────────
  // Lightweight, independent fetch — mirrors what MyCohortsPage.jsx does
  // (GET /api/cohorts returns every cohort this user belongs to, with
  // their role). We only need the ID list here, to feed useFilters' new
  // 'my_cohorts' quick filter. Non-fatal on failure: the filter just
  // matches nothing, same as any other quick filter would with no
  // underlying data.
  const [userCohortIds, setUserCohortIds] = useState([]);
  useEffect(() => {
    if (!isAuthenticated) { setUserCohortIds([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/cohorts');
        if (cancelled) return;
        const ids = (res.data?.cohorts || []).map(c => c.id);
        setUserCohortIds(ids);
      } catch {
        if (!cancelled) setUserCohortIds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const filters   = useFilters({
    isAuthenticated,
    userId: user?.id,
    mapData,
    starredIds: starred.starredIds,
    platformFilters,
    userCohortIds,
  });
  const overlays  = useOverlays();
  const lens = useLens(mapData.projects || []);

  // ── Links on the map: mention lines + Use echoes ───────────────────────
  // refreshKey = the projects array, so a link saved in the create/edit
  // overlay appears as soon as the map data refetches.
  const links = useMapLinks({ enabled: isAuthenticated, refreshKey: mapData.projects });

  const {
    deepLinkProjectId, deepLinkFocusId, deepLinkChallengeId, deepLinkChannelId,
    deepLinkBranchId, shouldHighlightLineage, shouldOpenCreate, shouldOpenCreateChallenge,
    deepLinkMultiSelect, deepLinkMultiAction,
    clearDeepLink,
  } = useDeepLink();

  // Deep links
  useEffect(() => {
    if (!deepLinkProjectId || !mapData?.projects) return;
    const target = mapData.projects.find(p => p.id === deepLinkProjectId);
    if (!target) return;
    selection.setSelectedProject(target);
    overlays.openProjectDetail();
    if (shouldHighlightLineage) selection.setHighlightedIds(lineage.computeLineage(target));
    clearDeepLink();
  }, [deepLinkProjectId, mapData, shouldHighlightLineage]);

  // Multi-select from Explore/SurpriseMe feed selection bar
  useEffect(() => {
    if (!deepLinkMultiSelect?.length || !mapData?.projects) return;
    const projects = deepLinkMultiSelect
      .map(id => mapData.projects.find(p => p.id === id))
      .filter(Boolean);
    if (projects.length === 0) return;

    selection.enterMultiSelect();
    selection.setMultiSelectedIds(deepLinkMultiSelect);

    if (deepLinkMultiAction === 'fork') {
      // Open create overlay with selected projects as parents
      overlays.openCreateProject({ mode: 'fork', parents: projects });
    } else if (deepLinkMultiAction === 'collect') {
      // Open curate overlay with selected projects
      overlays.openCurate?.();
    }
    clearDeepLink();
  }, [deepLinkMultiSelect, deepLinkMultiAction, mapData]);

  useEffect(() => {
    if (!deepLinkFocusId || !mapData?.projects) return;
    const target = mapData.projects.find(p => p.id === deepLinkFocusId);
    if (!target) return;
    selection.setSelectedProject(target);
    selection.setHighlightedIds(lineage.computeLineage(target));
    clearDeepLink();
  }, [deepLinkFocusId, mapData]);

  useEffect(() => {
    if (deepLinkChannelId) { overlays.toggleMessaging(true); clearDeepLink(); }
  }, [deepLinkChannelId]);

  useEffect(() => {
    if (!deepLinkBranchId || !branches.myBranches.length) return;
    const branch = [...branches.myBranches, ...branches.featuredBranches].find(b => b.id === deepLinkBranchId);
    if (branch) { branches.selectBranch(branch); clearDeepLink(); }
  }, [deepLinkBranchId, branches.myBranches, branches.featuredBranches]);

  useEffect(() => {
    if (!shouldOpenCreate) return;
    overlays.openCreateProject();
    clearDeepLink();
  }, [shouldOpenCreate]);

  useEffect(() => {
    if (!shouldOpenCreateChallenge) return;
    if (selection.selectedProject) {
      overlays.openCreateChallenge(selection.selectedProject);
    } else {
      overlays.openCreateProject();
    }
    clearDeepLink();
  }, [shouldOpenCreateChallenge]);

  // ── Branch → Selection coordination ────────────────────────────────────
  useEffect(() => {
    if (branches.activeBranch) {
      selection.setHighlightedIds(branches.activeBranch.curatedProjectIds || []);
    }
  }, [branches.activeBranch?.id, branches.activeBranch?.curatedProjectIds?.length]);

  // ── Recently viewed projects (hydrated from TrayContext IDs) ───────────
  const recentProjects = React.useMemo(() => {
    if (!recent.recentProjectIds.length) return [];
    return recent.recentProjectIds
      .map(id => (mapData.projects || []).find(p => p.id === id))
      .filter(Boolean);
  }, [recent.recentProjectIds, mapData.projects]);

  // ── Map event handlers ─────────────────────────────────────────────────

  // ── Map behaviour ──────────────────────────────────────────────────────
  // Shared with CohortDashboard via useMapController. Previously these five
  // handlers were written out here AND re-implemented in the cohort map,
  // and the two drifted — a wrong connection shape and a missing highlight
  // reset both shipped because of it.
  //
  // Dashboard INJECTS its own viewport/selection/lineage/scrubber rather
  // than letting the controller create them. Its scrubber comes from
  // TrayContext and is shared with SelectionTray; a second private
  // instance would leave the Journey panel reading a scrubber nobody
  // writes to.
  const mapController = useMapController({
    projects: filters.filteredData.projects,
    connections: filters.filteredData.connections,
    challenges: filters.filteredData.challenges,
    activeZones: filters.activeZones,
    showChallenges: filters.showChallenges,
    isLoading: mapLoading,

    viewport,
    selection,
    lineage,
    scrubber,

    // Dashboard opens the scrubber on a plain node click; the cohort map
    // does not. Explicit flag, so neither page inherits the other's
    // behaviour by accident.
    openScrubberOnSelect: true,

    onProjectSelected: recent.recordRecentProject,
    onOpenProject: (project) => handleProjectDoubleClick(project),
    onOpenChallenge: (challenge) => {
      selection.setSelectedChallenge(challenge);
      overlays.openChallengeDetail();
    },

    // Branch editing takes over a node click entirely: while you are
    // editing one of your own collections, shift-click edits its contents
    // instead of building a fork selection.
    interceptProjectClick: (project, event) => {
      if (!(branches.activeBranch
            && branches.activeBranch.creatorId === user?.id
            && (event?.shiftKey || selection.isMultiSelectMode))) {
        return false;
      }
      const ids = new Set(selection.multiSelectedIds);
      ids.has(project.id) ? ids.delete(project.id) : ids.add(project.id);
      const newIds = Array.from(ids);
      selection.setMultiSelectedIds(newIds);
      selection.setHighlightedIds(newIds);
      branches.setActiveBranch(prev =>
        prev ? { ...prev, curatedProjectIds: newIds, curatedCount: newIds.length } : null
      );
      return true;
    },

    extraMapProps: {
      lensWarmthMap: lens.warmthMap,
      lensProbeId: lens.probeProjectId,
      isLensActive: lens.isLensActive,
      lensResults: lens.lensResults,
      // -- Collection illumination -------------------------------
      // Blue reach + glow for the collection currently being viewed.
      // activeBranch already drives highlightedIds via the Branch ->
      // Selection effect above, which dims every non-member; this adds
      // the blue on top of that dimming.
      //
      // Blue and fuchsia are meant to coexist: blue = IS in the
      // collection, fuchsia = RESEMBLES it. Lensing a collection you
      // are viewing shows both at once, which is the "what should I
      // add next?" read.
      collectionMemberIds: branches.activeBranch?.curatedProjectIds || [],
      isCollectionActive: Boolean(branches.activeBranch),
    },
  });

  // Keep the local connectionInfo card in sync with the controller.
  useEffect(() => {
    setConnectionInfo(mapController.connectionInfo);
  }, [mapController.connectionInfo]);

  const handleProjectDoubleClick = useCallback(async (project) => {
    if (selection.isMultiSelectMode) return;
    selection.setSelectedProject(project);
    recent.recordRecentProject(project);
    overlays.openProjectDetail();
    scrubber.closeScrubber();
    try {
      const res = await (projectsAPI.getContainingBranches
        ? projectsAPI.getContainingBranches(project.id)
        : api.get(`/projects/${project.id}/containing-branches`));
      overlays.setProjectCuratedBranches(res.data?.branches || []);
    } catch { overlays.setProjectCuratedBranches([]); }
  }, [selection.isMultiSelectMode]);

  // Challenge click needs Dashboard's richer scrubber chain (parent +
  // challenge card + responses), which the controller does not build.
  // Wrapped rather than replaced.
  const handleChallengeClick = useCallback((challenge) => {
    mapController.mapProps.onChallengeClick(challenge);

    const parentId = challenge.project_id || challenge.projectId;
    const responseIds = challenge.responseProjectIds || challenge.response_project_ids || [];
    const all = mapData.projects || [];
    const parentProject = parentId ? all.find(p => p.id === parentId) : null;
    const responseProjects = Array.isArray(responseIds)
      ? responseIds.map(rid => all.find(p => p.id === rid)).filter(Boolean)
      : [];

    const chain = [
      ...(parentProject ? [parentProject] : []),
      {
        id: `challenge-${challenge.id}`,
        name: challenge.challenge_title || challenge.challengeTitle || 'Challenge',
        _isChallenge: true,
        _challengeData: challenge,
        zone: parentProject ? getProjectZone(parentProject).id : 'default',
        author: { username: challenge.requesterUsername || '' },
      },
      ...responseProjects,
    ];

    if (chain.length >= 1) scrubber.openScrubber(chain);
    else scrubber.closeScrubber();
  }, [mapController.mapProps, mapData.projects, scrubber]);

  // Background click clears page-level things the controller knows
  // nothing about: search, legend, active branch.
  const handleBackgroundClick = useCallback(() => {
    if (branches.activeBranch && selection.isMultiSelectMode) {
      selection.exitMultiSelect(branches.activeBranch.curatedProjectIds || []);
      return;
    }
    mapController.mapProps.onBackgroundClick();
    setConnectionInfo(null);
    filters.setSearchQuery('');
    overlays.setShowLegend(false);
    branches.clearBranch();
  }, [branches.activeBranch, selection.isMultiSelectMode, mapController.mapProps]);

  // ── Action handlers ────────────────────────────────────────────────────

  const handleFork = useCallback((project) => {
    overlays.closeProjectDetail();
    overlays.openCreateProject({ mode: 'fork', parents: [project] });
  }, []);

  const handleForkSelected = useCallback(() => {
    overlays.openCreateProject({ mode: 'multifork', parents: selection.multiSelectedProjects });
  }, [selection.multiSelectedProjects]);

  const handleCreateChallenge = useCallback((project) => {
    overlays.closeProjectDetail();
    overlays.openCreateChallenge(project);
  }, []);

  const handleRespondToChallenge = useCallback((challenge) => {
    overlays.closeChallengeDetail();
    overlays.openCreateProject({ mode: 'challenge-response', challenge });
  }, []);

  const handleCreateProject = useCallback(() => overlays.openCreateProject(), []);

  const handleEditProject = useCallback((project) => {
    const files = [
      ...(project.digital_items || []).map(item => ({
        id: item.id, name: item.name || item.file_name,
        file_url: item.file_url, file_type: item.file_type, type: 'digital',
      })),
      ...(project.physical_items || []).map(item => ({
        id: item.id, name: item.name, image_url: item.image_url, type: 'physical',
      })),
    ];
    overlays.closeProjectDetail();
    overlays.openCreateProject({ mode: 'edit', project: { ...project, files } });
  }, []);

  const handleCreateSuccess = useCallback((newProject) => {
    const wasEdit = overlays.createMode === 'edit';
    toast.success(wasEdit ? 'Project updated!' : 'Project created!');
    overlays.closeCreateProject();
    if (!wasEdit) selection.clearAll();
    scrubber.closeScrubber();
    refetch();
    if (!wasEdit && newProject && overlays.forkParents?.length > 0) {
      const parentProject = overlays.forkParents[0];
      try {
        const parentZone = getProjectZone(parentProject);
        const childZone  = getProjectZone(newProject);
        if (parentZone.id !== childZone.id) {
          setCelebration({ sourceZone: parentZone.id, targetZone: childZone.id, sourceName: parentProject.name, targetName: newProject.name });
        }
      } catch {}
    }
  }, [refetch, overlays.forkParents]);

  const handleCurateSuccess = useCallback((newBranch) => {
    toast.success('Curated branch created!');
    overlays.closeCurate();
    selection.clearAll();
    branches.addBranch(newBranch);
    refetch();
  }, [refetch]);

  const handleSearchProjects = useCallback(async (query) => {
    return searchProjects(query);
  }, [searchProjects]);

  const handleScrubberSelect = useCallback((project) => {
    selection.setSelectedProject(project);
    selection.setHighlightedIds(lineage.computeLineage(project));
    recent.recordRecentProject(project);
  }, [lineage]);

  const handleDeleteProject = useCallback(async (project) => {
    try {
      await projectsAPI.delete(project.id);
      toast.success('Project deleted');
      overlays.closeProjectDetail();
      selection.setSelectedProject(null);
      refetch();
    } catch { toast.error('Failed to delete project'); }
  }, [refetch]);

  // ── SelectionTray handlers ─────────────────────────────────────────────

  const handleTrayAddProject = useCallback((project) => {
    if (!selection.isMultiSelectMode) selection.enterMultiSelect();
    selection.setMultiSelectedIds(prev => prev.includes(project.id) ? prev : [...prev, project.id]);
    selection.setHighlightedIds(prev => prev.includes(project.id) ? prev : [...prev, project.id]);
    if (branches.activeBranch) {
      branches.setActiveBranch(prev =>
        prev ? {
          ...prev,
          curatedProjectIds: [...new Set([...(prev.curatedProjectIds || []), project.id])],
          curatedCount: (prev.curatedCount || 0) + 1,
        } : null
      );
    }
  }, [selection.isMultiSelectMode, branches.activeBranch]);

  const handleTrayRemoveProject = useCallback((project) => {
    selection.setMultiSelectedIds(prev => prev.filter(id => id !== project.id));
    selection.setHighlightedIds(prev => prev.filter(id => id !== project.id));
    if (branches.activeBranch) {
      branches.setActiveBranch(prev =>
        prev ? {
          ...prev,
          curatedProjectIds: (prev.curatedProjectIds || []).filter(id => id !== project.id),
          curatedCount: Math.max(0, (prev.curatedCount || 0) - 1),
        } : null
      );
    }
  }, [branches.activeBranch]);

  const handleTrayOpenProject = useCallback((project) => {
    selection.setSelectedProject(project);
    recent.recordRecentProject(project);
    overlays.openProjectDetail();
  }, []);

  const handleTrayExitMultiSelect = useCallback(() => {
    if (branches.activeBranch) {
      selection.exitMultiSelect(branches.activeBranch.curatedProjectIds || []);
    } else {
      selection.exitMultiSelect();
    }
  }, [branches.activeBranch]);

  const handleLoadBranchToSelection = useCallback((projectIds) => {
    selection.enterMultiSelect();
    selection.setMultiSelectedIds(projectIds);
    selection.setHighlightedIds(projectIds);
  }, []);

  const handleTrayJourneySelect = useCallback((project) => {
    selection.setSelectedProject(project);
    selection.setHighlightedIds(lineage.computeLineage(project));
    recent.recordRecentProject(project);
  }, [lineage]);

  const handleTrayJourneyView = useCallback((project) => {
    selection.setSelectedProject(project);
    recent.recordRecentProject(project);
    overlays.openProjectDetail();
  }, []);

  const handleTrayJourneyClose = useCallback(() => {
    scrubber.closeScrubber();
  }, []);

  const handleFlyToStarred = React.useCallback((project) => {
    selection.setSelectedProject(project);
    selection.setSelectedChallenge(null);
    recent.recordRecentProject(project);
    const chain = lineage.computeLineage(project);
    selection.setHighlightedIds(chain);
    if (chain.length >= 1) scrubber.openScrubber(lineage.buildLineageChain(project));
  }, [selection, lineage, scrubber, recent.recordRecentProject]);

  // ── Branch action wrappers ─────────────────────────────────────────────

  const handleSelectBranch = useCallback((branch) => {
    branches.selectBranch(branch);
    if (branches.activeBranch?.id === branch.id) {
      selection.setHighlightedIds([]);
    }
  }, [branches.activeBranch]);

  const handleClearBranch = useCallback(() => {
    branches.clearBranch();
    selection.setHighlightedIds([]);
  }, []);

  const handleEditBranch = useCallback((branch) => {
    const ids = branches.editBranch(branch);
    selection.setHighlightedIds(ids);
    selection.enterMultiSelect();
    selection.setMultiSelectedIds(ids);
  }, []);

  const handleDeleteBranch = useCallback(async (branch) => {
    const deleted = await branches.deleteBranch(branch);
    if (deleted && branches.activeBranch?.id === branch.id) {
      selection.clearAll();
    }
  }, [branches.activeBranch]);

  const handleSaveBranchEdits = useCallback(async () => {
    const newIds = await branches.saveBranchEdits(selection.multiSelectedIds);
    if (newIds) {
      selection.exitMultiSelect();
      selection.setHighlightedIds(newIds);
    }
  }, [selection.multiSelectedIds]);

  const handleFlyToProject = React.useCallback((project) => {
    selection.setSelectedProject(project);
    selection.setSelectedChallenge(null);
    recent.recordRecentProject(project);
    selection.setHighlightedIds(lineage.computeLineage(project));
  }, [selection, lineage, recent.recordRecentProject]);

  const handleRemoveFromBranch = React.useCallback(async (branchId, projectId) => {
    branches.setActiveBranch(prev => {
      if (!prev || prev.id !== branchId) return prev;
      const newIds = (prev.curatedProjectIds || []).filter(id => id !== projectId);
      return { ...prev, curatedProjectIds: newIds, curatedCount: newIds.length };
    });
    try {
      const branch = [...branches.myBranches, ...branches.featuredBranches].find(b => b.id === branchId);
      if (branch) {
        const newIds = (branch.curatedProjectIds || []).filter(id => id !== projectId);
        await branches.saveBranchEdits(newIds, branchId);
      }
    } catch { toast.error('Could not remove project from collection'); refetch(); }
  }, [branches]);

  const handleRenameBranch = React.useCallback(async (branchId, newName) => {
      await branches.renameBranch(branchId, newName);
}, [branches]);

  // ── Auth redirect ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated && !mapLoading) navigate('/login');
  }, [isAuthenticated, mapLoading, navigate]);

  // No full-page spinner — let CosmicMap render its skeleton loading state
  // so users see the cosmic map coming to life on first visit.
  // isLoading={mapLoading} is already passed to CosmicMap.

  if (mapError) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">{mapError}</p>
          <Button onClick={refetch}>Try Again</Button>
        </div>
      </div>
    );
  }

  const projectCount      = (filters.filteredData.projects || []).length;
  const totalProjectCount = (mapData.projects || []).length;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="relative w-full h-full bg-zinc-950 overflow-hidden">

      {showTour && (
        <MapGuidedTour
          onComplete={completeTour}
          projects={filters.filteredData.projects}
          connections={filters.filteredData.connections}
          selectedProjectId={selection.selectedProject?.id}
          showScrubber={scrubber.showScrubber}
          highlightedIds={selection.highlightedIds}
        />
      )}

      {celebration && (
        <CrossZoneCelebration
          sourceZone={celebration.sourceZone}
          targetZone={celebration.targetZone}
          sourceName={celebration.sourceName}
          targetName={celebration.targetName}
          onDismiss={() => setCelebration(null)}
        />
      )}

      {connectionInfo && (
        <ConnectionInfoCard
          source={connectionInfo.source}
          target={connectionInfo.target}
          onClose={() => setConnectionInfo(null)}
          onViewProject={(project) => {
            setConnectionInfo(null);
            selection.setSelectedProject(project);
            recent.recordRecentProject(project);
            overlays.openProjectDetail();
          }}
        />
      )}

      <CosmicMap
        {...mapController.mapProps}
        onChallengeClick={handleChallengeClick}
        onBackgroundClick={handleBackgroundClick}
        mentionEdges={links.mentionEdges}
        useEchoes={links.useEchoes}
        showLinks={links.showLinks}
        showEchoes={links.showEchoes}
      />

      {/* ── "Show links" pill — mention lines only, shown when any exist ── */}
      {!showTour && links.hasMentions && (
        <button
          type="button"
          onClick={() => links.setShowLinks(v => !v)}
          aria-pressed={links.showLinks}
          title={links.showLinks
            ? 'Showing every mention line. Click to show them only for the project you select.'
            : 'Show every mention line on the map'}
          className={cn(
            'absolute left-4 bottom-24 md:bottom-28 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full',
            'text-[12px] font-medium backdrop-blur-xl border transition-all active:scale-95',
            links.showLinks
              ? 'bg-sky-500/15 text-sky-300 border-sky-500/40'
              : 'bg-zinc-900/70 text-zinc-400 border-zinc-700/60 hover:text-zinc-200'
          )}
        >
          {/* Dotted line glyph: the same visual language as the mention lines */}
          <svg width="18" height="8" viewBox="0 0 18 8" aria-hidden="true">
            <path d="M1 6 Q9 -2 17 6" fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeDasharray="0.5 3.5" />
          </svg>
          {links.showLinks ? 'Links on' : 'Show links'}
        </button>
      )}

      {!showTour && (
        <MapControls
          zoom={viewport.zoom}
          onZoomIn={viewport.zoomIn}
          onZoomOut={viewport.zoomOut}
          onReset={viewport.resetView}
          searchQuery={filters.searchQuery}
          onSearchChange={filters.setSearchQuery}
          allProjects={mapData.projects || []}
          allChallenges={mapData.challenges || []}
          activeZones={filters.activeZones}
          onToggleZone={filters.toggleZone}
          showChallenges={filters.showChallenges}
          onToggleChallenges={() => filters.setShowChallenges(v => !v)}
          activeQuickFilters={filters.activeQuickFilters}
          onToggleQuickFilter={filters.toggleQuickFilter}
          activeTypeFilters={filters.activeTypeFilters}
          onToggleTypeFilter={filters.toggleTypeFilter}
          activeShapeFilters={filters.activeShapeFilters}
          onToggleShapeFilter={filters.toggleShapeFilter}
          sortBy={filters.sortBy}
          onSortChange={filters.setSortBy}
          starredIds={starred.starredIds}
          onStarProject={starred.starProject}
          onUnstarProject={starred.unstarProject}
          myBranches={branches.myBranches}
          featuredBranches={branches.featuredBranches}
          activeBranch={branches.activeBranch}
          onSelectBranch={handleSelectBranch}
          onClearBranch={handleClearBranch}
          onEditBranch={handleEditBranch}
          onDeleteBranch={handleDeleteBranch}
          filteredProjectCount={projectCount}
          totalProjectCount={totalProjectCount}
          onFlyToProject={(project) => {
            selection.setHighlightedIds([project.id]);
            selection.setSelectedProject(project);
            recent.recordRecentProject(project);
          }}
          onSelectProject={(project) => {
            selection.setSelectedProject(project);
            recent.recordRecentProject(project);
            overlays.openProjectDetail();
          }}
          onFlyToChallenge={(challenge) => {
            const parentId = challenge.project_id || challenge.projectId;
            const responseIds = challenge.responseProjectIds || challenge.response_project_ids || [];
            const ids = [
              `c-${challenge.id}`,
              ...(parentId ? [parentId] : []),
              ...(Array.isArray(responseIds) ? responseIds : []),
            ];
            selection.setHighlightedIds(ids);
          }}
          onSelectChallenge={(challenge) => {
            selection.setSelectedChallenge(challenge);
            overlays.openChallengeDetail();
          }}
          viewMode={viewport.viewMode}
          onViewModeChange={viewport.setViewMode}
          onCreateProject={handleCreateProject}
          onToggleLegend={overlays.toggleLegend}
          showLegend={overlays.showLegend}
          currentUserId={user?.id}
          isTrayExpanded={isTrayExpanded}
          isLensActive={lens.isLensActive}
          lensProbeProject={lens.probeProject}
          onActivateLens={() => lens.activateWithProject(selection.selectedProject)}
          onDeactivateLens={lens.deactivate}
          showEchoes={links.showEchoes}
          onToggleEchoes={() => links.setShowEchoes(v => !v)}
          echoCount={links.echoCount}
        />
      )}

      {!showTour && !overlays.dismissedGettingStarted && (
        <GettingStartedCard
          projectCount={projectCount}
          isLoading={mapLoading}
          onDismiss={() => overlays.setDismissedGettingStarted(true)}
          onCreateProject={handleCreateProject}
        />
      )}

      <MapLegend show={overlays.showLegend} onClose={() => overlays.setShowLegend(false)} />

      <MultiSelectActionBar
        isMultiSelectMode={selection.isMultiSelectMode}
        hasHighlightedLineage={selection.highlightedIds.length > 0}
        onEnterMultiSelect={selection.enterMultiSelect}
      />

      {/* ====== OVERLAYS ====== */}

      {overlays.showProjectDetail && selection.selectedProject && (
        <OverlayErrorBoundary onClose={overlays.closeProjectDetail} name="Project Details" resetKey={selection.selectedProject?.id}>
          <ProjectDetailOverlay
            project={selection.selectedProject}
            parentProjects={(mapData.projects || []).filter(p =>
              selection.selectedProject.parent_project_ids?.includes(p.id) ||
              p.id === selection.selectedProject.parent_project_id
            )}
            childProjects={(mapData.projects || []).filter(p =>
              p.parent_project_id === selection.selectedProject.id ||
              (p.parent_project_ids || []).includes(selection.selectedProject.id)
            )}
            curatedBranches={overlays.projectCuratedBranches}
            onClose={overlays.closeProjectDetail}
            onFork={() => handleFork(selection.selectedProject)}
            onCreateChallenge={() => handleCreateChallenge(selection.selectedProject)}
            onEdit={(proj) => handleEditProject(proj || selection.selectedProject)}
            onDelete={handleDeleteProject}
            onViewProject={(project) => {
              selection.setSelectedProject(project);
              recent.recordRecentProject(project);
            }}
            onViewBranch={(branch) => {
              const ids = branch.curatedProjectIds || [];
              if (ids.length > 0) {
                branches.setActiveBranch(branch);
                selection.setHighlightedIds(ids);
                overlays.closeProjectDetail();
                toast.success(`Viewing branch: ${branch.name || 'Unnamed'}`, { icon: '\uD83C\uDF3F' });
              }
            }}
          />
        </OverlayErrorBoundary>
      )}

      {overlays.showChallengeDetail && selection.selectedChallenge && (
        <OverlayErrorBoundary onClose={overlays.closeChallengeDetail} name="Challenge Details" resetKey={selection.selectedChallenge?.id}>
          <ChallengeDetailOverlay
            challenge={selection.selectedChallenge}
            originProject={(mapData.projects || []).find(p =>
              p.id === (selection.selectedChallenge.project_id || selection.selectedChallenge.projectId)
            )}
            onClose={overlays.closeChallengeDetail}
            onRespond={() => handleRespondToChallenge(selection.selectedChallenge)}
            onViewProject={(project) => {
              overlays.closeChallengeDetail();
              selection.setSelectedProject(project);
              recent.recordRecentProject(project);
              overlays.openProjectDetail();
            }}
          />
        </OverlayErrorBoundary>
      )}

      {overlays.showCreateChallenge && overlays.challengeProjectContext && (
        <OverlayErrorBoundary onClose={overlays.closeCreateChallenge} name="Create Challenge">
          <CreateChallengeOverlay
            project={overlays.challengeProjectContext}
            onClose={overlays.closeCreateChallenge}
            onSuccess={() => { overlays.closeCreateChallenge(); refetch(); }}
          />
        </OverlayErrorBoundary>
      )}

      {overlays.showCreateProject && (
        <OverlayErrorBoundary onClose={overlays.closeCreateProject} name="Create Project" resetKey={overlays.createMode}>
          <CreateProjectOverlay
            mode={overlays.createMode}
            parentProjects={overlays.forkParents}
            challenge={overlays.responseChallenge}
            editProject={overlays.editProject}
            onClose={overlays.closeCreateProject}
            onSuccess={handleCreateSuccess}
            onSearchProjects={handleSearchProjects}
          />
        </OverlayErrorBoundary>
      )}

      {overlays.showCurateOverlay && (
        <OverlayErrorBoundary onClose={overlays.closeCurate} name="Curate Branch">
          <CurateOverlay
            selectedProjects={selection.multiSelectedProjects}
            onClose={overlays.closeCurate}
            onSuccess={handleCurateSuccess}
          />
        </OverlayErrorBoundary>
      )}

      {/* ====== SELECTION TRAY (bottom workspace) ====== */}

      <SelectionTray
        isVisible
        alwaysShow
        onExpandChange={setIsTrayExpanded}
        selectedProjects={selection.multiSelectedProjects}
        multiSelectedIds={selection.multiSelectedIds}
        isMultiSelectMode={selection.isMultiSelectMode}
        onRemoveProject={handleTrayRemoveProject}
        onAddProject={handleTrayAddProject}
        onClearAll={() => { selection.clearMultiSelection(); selection.setHighlightedIds([]); }}
        onForkSelected={handleForkSelected}
        onCurateSelected={() => overlays.openCurate()}
        onExitMultiSelect={handleTrayExitMultiSelect}
        onEnterMultiSelect={selection.enterMultiSelect}
        activeBranch={branches.activeBranch}
        onSaveBranchEdits={handleSaveBranchEdits}
        onSearchProjects={handleSearchProjects}
        starredProjects={starred.starredIds.map(id => (mapData.projects || []).find(p => p.id === id)).filter(Boolean)}
        recentProjects={recentProjects}
        highlightedIds={selection.highlightedIds}
        myBranches={branches.myBranches}
        featuredBranches={branches.featuredBranches}
        currentUserId={user?.id}
        onFlyToStarred={handleFlyToStarred}
        onFlyToRecent={handleFlyToStarred}
        onUnstar={(projectId) => starred.unstarProject(projectId)}
        onActivateBranch={handleSelectBranch}
        onEditBranch={handleEditBranch}
        onLoadBranchToSelection={handleLoadBranchToSelection}
        allProjects={mapData.projects || []}
        onFlyToProject={handleFlyToProject}
        onOpenProject={handleTrayOpenProject}
        onRemoveFromBranch={handleRemoveFromBranch}
        onRenameBranch={handleRenameBranch}
        onUpdateBranchProjects={branches.updateBranchProjects}
        onDeleteBranch={handleDeleteBranch}
        journeyLineage={scrubber.scrubberLineage}
        journeyProject={selection.selectedProject}
        onJourneySelect={handleTrayJourneySelect}
        onJourneyView={handleTrayJourneyView}
        onJourneyGrab={handleTrayAddProject}
        onJourneyClose={handleTrayJourneyClose}
        totalProjectCount={totalProjectCount}

        /* ── Context-aware collections sort (Option A) ───────────────────
         * When a project is clicked on the map, SelectionTray uses these
         * props to rank the Collections list by relevance:
         *   1. Containment (collection literally includes this project)
         *   2. Zone affinity (how many projects share this project's zone)
         *   3. Lineage proximity (ancestors/descendants present)
         * If contextProject is null, list falls back to default order.
         */
        contextProject={selection.selectedProject}
        contextLineageIds={
          selection.selectedProject
            ? lineage.computeLineage(selection.selectedProject)
            : []
        }
      />

      <MessagingDrawer
        isOpen={overlays.showMessaging}
        onClose={() => overlays.toggleMessaging(false)}
        onNavigateToProject={(projectId) => {
          const project = mapData.projects?.find(p => p.id === projectId);
          if (project) {
            selection.setSelectedProject(project);
            recent.recordRecentProject(project);
            overlays.openProjectDetail();
            overlays.toggleMessaging(false);
          }
        }}
        initialChannelId={deepLinkChannelId}
      />
    </div>
  );
};

export default Dashboard;